// src/components/digital_twin/Anomalies3DLayer.js
// --- UPDATE: v2.0 — performance + correctness
//   • Anomalies without coordinates were skipped INSIDE the instance loop, leaving
//     their instance slots as identity matrices → phantom spheres at the scene
//     origin. They are now filtered out before the InstancedMesh is sized.
//   • The rAF loop ran forever whenever any anomaly existed (even with nothing to
//     animate). It now runs only while high/critical instances exist, pauses when
//     the tab is hidden, respects prefers-reduced-motion, and asks Mapbox to
//     repaint (map.triggerRepaint) so the pulse is actually rendered.
//   • Polling skips hidden tabs; stale responses are dropped; 404s stay quiet.
//   • Unchanged data (same ids/severities) does not rebuild the meshes.
//   • `visible` prop; anomalies can be injected via `anomalies` (skip fetching).
//   • Units: the twin's scene is in Mercator units (useDigitalTwin places assets at
//     mc.x − origin.x, z = 0). v1 used raw metres (radius 5, z 10), i.e. spheres
//     the size of the planet far above the map. Radius/height are now metres
//     converted with origin.meterInMercatorCoordinateUnits().
// --- END UPDATE
import { useEffect, useState, useCallback, useRef } from 'react';
import * as THREE from 'three';
import { MercatorCoordinate } from 'mapbox-gl';
import AnalyticsService from '../../services/analyticsService';

// ==================== DEBUG LOGGER ====================
const logger = {
  log: (...args) => console.log('[ANOMALIES_3D]', ...args),
  warn: (...args) => console.warn('[ANOMALIES_3D]', ...args),
  error: (...args) => console.error('[ANOMALIES_3D]', ...args),
};

// ==================== MATERIALS ====================
// Reuse materials for performance
const SEVERITY_MATERIALS = {
  low: new THREE.MeshStandardMaterial({
    color: 0xffff00,
    emissive: 0xffff00,
    emissiveIntensity: 0.4,
  }),
  medium: new THREE.MeshStandardMaterial({
    color: 0xff9900,
    emissive: 0xff9900,
    emissiveIntensity: 0.5,
  }),
  high: new THREE.MeshStandardMaterial({
    color: 0xff0000,
    emissive: 0xff0000,
    emissiveIntensity: 0.6,
  }),
  critical: new THREE.MeshStandardMaterial({
    color: 0xff0000,
    emissive: 0xff0000,
    emissiveIntensity: 0.8,
  }),
};

// Pulse intensity for animation
const PULSE_INTENSITY = {
  high: 1.2,
  critical: 1.25,
};

// Reusable geometries
const ANOMALY_GEOMETRY = new THREE.SphereGeometry(5, 16, 16);

// --- UPDATE
const hasCoords = (a) => Number.isFinite(Number(a?.latitude)) && Number.isFinite(Number(a?.longitude))
  && !(Number(a.latitude) === 0 && Number(a.longitude) === 0);
const signature = (list) => list.map(a => `${a.id}:${a.severity}:${a.latitude}:${a.longitude}`).join('|');
const prefersReducedMotion = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
// --- END UPDATE

// ==================== MAIN COMPONENT ====================
const Anomalies3DLayer = ({
  scene, origin, enableAutoRefresh = true, refreshInterval = 30000,
  // --- UPDATE
  map = null, visible = true, anomalies: injectedAnomalies = null,
  // --- END UPDATE
}) => {
  const [anomalies, setAnomalies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Ref to store data needed for animation: { mesh, index, basePosition, intensity }
  const animatedInstancesRef = useRef([]);

  const refreshTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const anomaliesGroupRef = useRef(null);
  // --- UPDATE
  const requestIdRef = useRef(0);
  const lastSignatureRef = useRef('');
  const [animationVersion, setAnimationVersion] = useState(0);
  // --- END UPDATE

  // ==================== ENSURE ANOMALIES GROUP ====================
  const ensureAnomaliesGroup = useCallback(() => {
    if (!scene) return null;

    if (!anomaliesGroupRef.current) {
      let group = scene.getObjectByName('anomalies');
      if (!group) {
        group = new THREE.Group();
        group.name = 'anomalies';
        group.renderOrder = 500; // Render above most objects
        scene.add(group);
      }
      anomaliesGroupRef.current = group;
    }

    return anomaliesGroupRef.current;
  }, [scene]);

  // ==================== FETCH ANOMALIES ====================
  const fetchAnomalies = useCallback(async () => {
    if (!scene || !origin) return;
    // --- UPDATE: skip hidden tabs; injected data needs no fetch
    if (injectedAnomalies) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const requestId = ++requestIdRef.current;
    // --- END UPDATE

    setIsLoading(true);

    try {
      const data = await AnalyticsService.getAnomalies();

      // --- UPDATE: drop stale responses
      if (!isMountedRef.current || requestId !== requestIdRef.current) return;
      const list = (Array.isArray(data) ? data : data?.data || []).filter(hasCoords);
      const sig = signature(list);
      if (sig === lastSignatureRef.current) return; // nothing changed → keep meshes
      lastSignatureRef.current = sig;
      setAnomalies(list);
      // --- END UPDATE
    } catch (error) {
      // --- FIX: Silently ignore 404s to stop console flooding ---
      if (error.message?.includes('404') || error.status === 404) {
          logger.warn('Anomalies endpoint pending implementation (404). Silently skipping.');
      } else {
          logger.error('Error fetching anomalies:', error);
      }
      if (isMountedRef.current && requestId === requestIdRef.current && lastSignatureRef.current !== '') {
        lastSignatureRef.current = '';
        setAnomalies([]);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [scene, origin, injectedAnomalies]);

  // --- UPDATE: injected anomalies bypass the fetch
  useEffect(() => {
    if (!injectedAnomalies) return;
    const list = injectedAnomalies.filter(hasCoords);
    const sig = signature(list);
    if (sig !== lastSignatureRef.current) {
      lastSignatureRef.current = sig;
      setAnomalies(list);
    }
  }, [injectedAnomalies]);
  // --- END UPDATE

  // ==================== DRAW ANOMALIES (OPTIMIZED) ====================
  const drawAnomalies = useCallback(() => {
    if (!scene || !origin) return;

    const anomaliesGroup = ensureAnomaliesGroup();
    if (!anomaliesGroup) return;

    // 1. Clear existing objects and reset animation refs
    // (shared geometry/materials are module-level and must not be disposed here)
    animatedInstancesRef.current = [];
    while (anomaliesGroup.children.length > 0) {
      const child = anomaliesGroup.children[0];
      // --- UPDATE: InstancedMesh holds GPU instance buffers — release them
      if (typeof child.dispose === 'function') child.dispose();
      // --- END UPDATE
      anomaliesGroup.remove(child);
    }
    if (!anomalies.length) {
      map?.triggerRepaint?.();
      setAnimationVersion(v => v + 1);
      return;
    }

    // 2. Group anomalies by severity to create one InstancedMesh per material
    const buckets = { low: [], medium: [], high: [], critical: [] };

    for (const anomaly of anomalies) {
        const severity = anomaly.severity?.toLowerCase() || 'medium';
        if (buckets[severity]) {
            buckets[severity].push(anomaly);
        } else {
            buckets['medium'].push(anomaly);
        }
    }

    // 3. Create InstancedMesh for each bucket
    const dummy = new THREE.Object3D();
    // --- UPDATE: metres → scene (Mercator) units
    const mpu = typeof origin.meterInMercatorCoordinateUnits === 'function' ? origin.meterInMercatorCoordinateUnits() : 1;
    // --- END UPDATE

    Object.entries(buckets).forEach(([severity, items]) => {
        if (items.length === 0) return;

        const material = SEVERITY_MATERIALS[severity];
        const instancedMesh = new THREE.InstancedMesh(ANOMALY_GEOMETRY, material, items.length);
        instancedMesh.name = `anomalies-${severity}`;
        instancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); // Enable updates for animation

        items.forEach((anomaly, index) => {
            // (coordinates validated upstream — every slot gets a real matrix)
            const mc = MercatorCoordinate.fromLngLat([Number(anomaly.longitude), Number(anomaly.latitude)]);
            const x = mc.x - origin.x;
            const y = mc.y - origin.y;
            const z = (anomaly.altitude || 10) * mpu;

            dummy.position.set(x, y, z);
            dummy.scale.setScalar(mpu);
            dummy.updateMatrix();
            instancedMesh.setMatrixAt(index, dummy.matrix);

            // If severity is high or critical, register for animation
            if (severity === 'high' || severity === 'critical') {
                animatedInstancesRef.current.push({
                    mesh: instancedMesh,
                    index: index,
                    basePosition: new THREE.Vector3(x, y, z),
                    baseScale: mpu,
                    intensity: PULSE_INTENSITY[severity] || 1.15
                });
            }
        });

        instancedMesh.instanceMatrix.needsUpdate = true;
        anomaliesGroup.add(instancedMesh);
    });

    // --- UPDATE
    map?.triggerRepaint?.();
    setAnimationVersion(v => v + 1);
    // --- END UPDATE
  }, [scene, origin, anomalies, ensureAnomaliesGroup, map]);

  // ==================== ANIMATION LOOP (MANUAL) ====================
  // --- UPDATE: only when there is something to animate, tab visible, motion allowed
  useEffect(() => {
    if (animatedInstancesRef.current.length === 0 || !visible || prefersReducedMotion()) return undefined;

    let animationId = null;
    const dummy = new THREE.Object3D();
    const meshes = [...new Set(animatedInstancesRef.current.map(a => a.mesh))];

    const animate = () => {
      animationId = requestAnimationFrame(animate);
      if (document.visibilityState === 'hidden') return;

      const time = Date.now() * 0.003; // Time factor
      // Sine wave pulsing: oscillates between 1.0 and intensity
      const pulseFactor = (Math.sin(time) + 1) / 2;

      animatedInstancesRef.current.forEach(({ mesh, index, basePosition, intensity, baseScale = 1 }) => {
          const scale = (1 + pulseFactor * (intensity - 1)) * baseScale;
          dummy.position.copy(basePosition);
          dummy.scale.setScalar(scale);
          dummy.updateMatrix();
          mesh.setMatrixAt(index, dummy.matrix);
      });

      meshes.forEach(mesh => {
          mesh.instanceMatrix.needsUpdate = true;
      });
      map?.triggerRepaint?.();
    };

    animate();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [animationVersion, visible, map]);
  // --- END UPDATE

  // ==================== INITIAL FETCH ====================
  useEffect(() => {
    isMountedRef.current = true;
    fetchAnomalies();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchAnomalies]);

  // ==================== DRAW WHEN DATA CHANGES ====================
  useEffect(() => {
    drawAnomalies();
  }, [drawAnomalies]);

  // --- UPDATE: visibility toggle without rebuilding
  useEffect(() => {
    const group = anomaliesGroupRef.current;
    if (group) {
      group.visible = !!visible;
      map?.triggerRepaint?.();
    }
  }, [visible, map, animationVersion]);
  // --- END UPDATE

  // ==================== AUTO REFRESH ====================
  useEffect(() => {
    if (enableAutoRefresh && refreshInterval > 0 && !injectedAnomalies) {
      refreshTimerRef.current = setInterval(() => {
        fetchAnomalies();
      }, refreshInterval);

      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
        }
      };
    }
    return undefined;
  }, [enableAutoRefresh, refreshInterval, fetchAnomalies, injectedAnomalies]);

  // ==================== CLEANUP ====================
  useEffect(() => {
    return () => {
      animatedInstancesRef.current = [];

      const anomaliesGroup = anomaliesGroupRef.current;
      if (anomaliesGroup) {
        while (anomaliesGroup.children.length > 0) {
          const child = anomaliesGroup.children[0];
          if (typeof child.dispose === 'function') child.dispose();
          anomaliesGroup.remove(child);
        }
      }
    };
  }, []);

  // isLoading is kept for parity with previous versions / debugging
  void isLoading;
  return null; // This component renders nothing in the React DOM
};

export default Anomalies3DLayer;